import { Button, Empty, Input, Space, Tooltip } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  PropRuntimeEvent,
  PropRuntimeEventType,
  RuntimePropInstance,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import type { SandboxEventLog } from "./types";
import {
  LogItem,
  LogCategoryChecks,
  LogFilterRow,
  LogTitle,
  QuietBadge,
  SandboxSection,
  SandboxSectionHeader,
  ScrollList,
} from "./styles";
import { formatSignedNumber, getRuntimeInstanceName, isRuntimeEmptySlot } from "./runtimeBridge";

interface EventLogPanelProps {
  manager?: BattleManager;
}

export function EventLogPanel({ manager }: EventLogPanelProps) {
  const [logs, setLogs] = useState<SandboxEventLog[]>([]);
  const [keyword, setKeyword] = useState("");
  const [categories, setCategories] = useState<SandboxEventCategory[]>(DEFAULT_EVENT_CATEGORIES);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLogs([]);
    if (!manager) return undefined;
    const eventBus = manager.getEventBus();
    const handleEvent = (event: PropRuntimeEvent) => {
      const log = createEventLog(event);
      if (!log) return;
      setLogs((previous) => event.type === "gameStarted" ? [log] : [...previous, log]);
    };
    const disposers = ALL_RUNTIME_EVENTS.map((type) => eventBus.on(type, handleEvent));
    return () => disposers.forEach((dispose) => dispose());
  }, [manager]);

  const visibleLogs = useMemo(
    () => logs.filter((log) => matchLogCategory(log, categories) && matchLogKeyword(log, keyword)),
    [categories, keyword, logs],
  );

  return (
    <SandboxSection $fill>
      <SandboxSectionHeader>
        <span>事件日志</span>
        <Space size={6} align="center">
          <LogCategoryChecks
            options={EVENT_CATEGORY_OPTIONS}
            value={categories}
            onChange={(values) => setCategories(values as SandboxEventCategory[])}
          />
          <QuietBadge count={visibleLogs.length} overflowCount={999} />
          <Tooltip title="清空日志">
            <Button
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => setLogs([])}
            />
          </Tooltip>
        </Space>
      </SandboxSectionHeader>
      <LogFilterRow>
        <Input
          size="small"
          allowClear
          placeholder="搜索事件"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />
      </LogFilterRow>
      <ScrollList ref={listRef}>
        {visibleLogs.map((log) => (
          <LogItem key={log.id}>
            <LogTitle>{log.summary}</LogTitle>
          </LogItem>
        ))}
        {!visibleLogs.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={logs.length ? "没有匹配事件" : "暂无事件"}
          />
        )}
      </ScrollList>
    </SandboxSection>
  );
}

type SandboxEventCategory = "flow" | "prop" | "magic" | "state";

const ALL_RUNTIME_EVENTS: PropRuntimeEventType[] = [
  "gameStarted",
  "gameEnded",
  "commissionStarted",
  "commissionEnded",
  "roundStarted",
  "roundEnded",
  "propAcquired",
  "propRemoved",
  "propEnteredBoard",
  "propLeftBoard",
  "propTimerChanged",
  "propEliminated",
  "propTransformed",
  "propPositionSwapped",
  "propUsed",
  "propCollected",
  "magicChanged",
  "rarityWeightChanged",
  "permanentBoostRulesChanged",
  "commissionRoundLimitChanged",
  "animationTriggered",
];

const EVENT_CATEGORY_OPTIONS: Array<{ value: SandboxEventCategory; label: string }> = [
  { value: "flow", label: "流程" },
  { value: "prop", label: "道具" },
  { value: "magic", label: "魔力" },
  { value: "state", label: "状态" },
];

const DEFAULT_EVENT_CATEGORIES = EVENT_CATEGORY_OPTIONS.map((item) => item.value);

const FLOW_EVENTS = new Set<string>([
  "gameStarted",
  "gameEnded",
  "commissionStarted",
  "commissionEnded",  "roundStarted",  "roundEnded",
]);

const MAGIC_EVENTS = new Set<string>(["magicChanged", "propCollected"]);

const STATE_EVENTS = new Set<string>([  "rarityWeightChanged",
  "permanentBoostRulesChanged",
  "commissionRoundLimitChanged",
  "propTimerChanged",
]);

const EMPTY_SLOT_LIFECYCLE_EVENTS = new Set<PropRuntimeEventType>([
  "propAcquired",
  "propRemoved",
  "propEnteredBoard",
  "propLeftBoard",
]);

const REDUNDANT_STATE_EVENTS = new Set<PropRuntimeEventType>([  "rarityWeightChanged",
  "permanentBoostRulesChanged",
  "commissionRoundLimitChanged",
]);

function createEventLog(event: PropRuntimeEvent): SandboxEventLog | undefined {
  if (shouldSkipEventLog(event)) return undefined;
  const itemName = readItemName(event.item);
  const targetName = readItemName(event.target);
  const sourceName = readItemName(event.source);
  const summary = addContextPrefix(event, summarizeEvent(event, itemName, targetName, sourceName));
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    totalRoundIndex: event.totalRoundIndex ?? 0,
    commissionIndex: event.commissionIndex ?? 0,
    roundIndex: event.roundIndex ?? 0,
    type: event.type,
    itemName,
    targetName,
    sourceName,
    magicDelta: event.magicDelta,
    summary,
    event,
  };
}

function shouldSkipEventLog(event: PropRuntimeEvent): boolean {
  if (REDUNDANT_STATE_EVENTS.has(event.type)) return true;
  if (!EMPTY_SLOT_LIFECYCLE_EVENTS.has(event.type)) return false;
  return isRuntimeEmptySlot(event.item) || isRuntimeEmptySlot(event.target);
}

function summarizeEvent(
  event: PropRuntimeEvent,
  itemName?: string,
  targetName?: string,
  sourceName?: string,
): string {
  switch (event.type) {
    case "gameStarted":
      return "游戏开始";
    case "gameEnded":
      return "游戏结束";
    case "commissionStarted":
      return "委托开始";
    case "commissionEnded":
      return "委托结束";
    case "roundStarted":
      return "回合开始";
    case "roundEnded":
      return "回合结束";
    case "propAcquired":
      return `获得 ${itemName ?? "-"}`;
    case "propRemoved":
      return `移除 ${itemName ?? "-"}`;
    case "propEnteredBoard":
      return `进场 ${itemName ?? "-"}${formatPosition(event)}`;
    case "propLeftBoard":
      return `离场 ${itemName ?? "-"}`;
    case "propTimerChanged":
      return typeof event.timerValue === "number"
        ? `${itemName ?? "-"} 计时器⌛️${event.timerValue}`
        : `${itemName ?? "-"} 计时器清除`;
    case "propEliminated":
      return sourceName
        ? `${sourceName} 消除 ${targetName ?? itemName ?? "-"}`
        : `消除 ${targetName ?? itemName ?? "-"}`;
    case "propTransformed":
      return `转化 ${event.previousPrototype?.name ?? event.previousPrototypeId ?? targetName ?? itemName ?? "-"} 为 ${event.prototype?.name ?? event.prototypeId ?? "-"}`;
    case "propPositionSwapped":
      return sourceName
        ? `${sourceName} 交换 ${targetName ?? itemName ?? "-"} 与 ${readItemName(event.relatedItem) ?? "-"} 位置`
        : `交换 ${targetName ?? itemName ?? "-"} 与 ${readItemName(event.relatedItem) ?? "-"} 位置`;
    case "propUsed":
      return `使用 ${itemName ?? "-"}`;
    case "propCollected":
      return `${sourceName ?? itemName ?? "-"} 从 ${targetName ?? "-"} 收集 ${event.magicDelta ?? 0} 魔力`;
    case "magicChanged":
      return sourceName
        ? `${sourceName} 魔力${formatSignedNumber(event.magicDelta ?? 0)}`
        : `魔力${formatSignedNumber(event.magicDelta ?? 0)}`;
    case "rarityWeightChanged":
      return sourceName ? `${sourceName} 品质权重变化` : "品质权重变化";
    case "permanentBoostRulesChanged":
      return sourceName ? `${sourceName} 永久加成变化` : "永久加成变化";
    case "commissionRoundLimitChanged":
      return sourceName ? `${sourceName} 调和轮数变化` : "调和轮数变化";
    case "animationTriggered":
      if (event.animationScope === "self") {
        return `${sourceName ?? itemName ?? "-"} 播放自身动画`;
      }
      return sourceName
        ? `${sourceName} 对 ${targetName ?? itemName ?? "-"} 播放目标动画`
        : `${targetName ?? itemName ?? "-"} 播放目标动画`;
    default:
      return "未知事件";
  }
}

function addContextPrefix(event: PropRuntimeEvent, text: string): string {
  const hasCommission = typeof event.commissionIndex === "number" && event.commissionIndex > 0;
  const hasRound = typeof event.roundIndex === "number" && event.roundIndex > 0;
  if (!hasCommission && !hasRound) return text;
  const commissionText = hasCommission ? String(event.commissionIndex) : "-";
  const roundText = hasRound ? String(event.roundIndex) : "-";
  return `[${commissionText}-${roundText}] ${text}`;
}

function formatPosition(event: PropRuntimeEvent): string {
  if (typeof event.row !== "number" || typeof event.col !== "number") return "";
  return ` [${event.row},${event.col}]`;
}

function readItemName(item?: RuntimePropInstance): string | undefined {
  if (!item) return undefined;
  return getRuntimeInstanceName(item);
}

function matchLogCategory(log: SandboxEventLog, categories: SandboxEventCategory[]): boolean {
  if (!categories.length) return true;
  return categories.some((category) => {
    if (category === "flow") return FLOW_EVENTS.has(log.type);
    if (category === "magic") return MAGIC_EVENTS.has(log.type);
    if (category === "state") return STATE_EVENTS.has(log.type);
    return !FLOW_EVENTS.has(log.type) && !MAGIC_EVENTS.has(log.type) && !STATE_EVENTS.has(log.type);
  });
}

function matchLogKeyword(log: SandboxEventLog, keyword: string): boolean {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) return true;
  return [
    log.summary,
    log.type,
    log.itemName,
    log.targetName,
    log.sourceName,
  ].some((value) => String(value ?? "").toLowerCase().includes(normalizedKeyword));
}
